import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Drawer } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  SettingOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { CONSOLE_THEME } from '@/shared/constants/colors';
import { FONT_WEIGHTS, TypographyUtils } from '@/shared/constants/typography';
import { IS_STG_DUMMY_SENDER_ENABLED } from '@/features/stg-dummy-sender/config/featureFlag';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// レイアウト定数
const LAYOUT_CONSTANTS = {
  SIDER_WIDTH: 240,
  DRAWER_WIDTH: 280,
} as const;

export const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { isMobile } = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'ダッシュボード',
      onClick: () => handleMenuClick('/dashboard'),
    },
    {
      key: '/users',
      icon: <TeamOutlined />,
      label: 'ユーザー管理',
      onClick: () => handleMenuClick('/users'),
    },
    {
      key: '/sites',
      icon: <EnvironmentOutlined />,
      label: '現場・盤管理',
      onClick: () => handleMenuClick('/sites'),
    },
    {
      key: '/archived-data',
      icon: <HistoryOutlined />,
      label: 'アーカイブ済みデータ',
      onClick: () => handleMenuClick('/archived-data'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '設定',
      onClick: () => handleMenuClick('/settings'),
    },
    ...(IS_STG_DUMMY_SENDER_ENABLED
      ? [
          {
            key: '/stg-dummy-sender',
            icon: <ExperimentOutlined />,
            label: 'STGダミー送信',
            onClick: () => handleMenuClick('/stg-dummy-sender'),
          },
        ]
      : []),
  ];

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ログアウト',
      onClick: handleLogout,
    },
  ];

  const renderSideMenu = () => (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      style={{ background: CONSOLE_THEME.BACKGROUND }}
    />
  );

  return (
    <Layout className="min-h-screen">
      {/* デスクトップ・タブレット用サイドバー */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="dark"
          width={LAYOUT_CONSTANTS.SIDER_WIDTH}
          style={{ background: CONSOLE_THEME.BACKGROUND }}
        >
          <div className="h-16 flex items-center justify-center text-white text-xl font-bold">
            {collapsed ? 'FC' : 'FARO Console'}
          </div>
          {renderSideMenu()}
        </Sider>
      )}

      {/* モバイル用ドロワー */}
      {isMobile && (
        <Drawer
          title={
            <span style={{ color: CONSOLE_THEME.TEXT, ...TypographyUtils.getFontSizeStyle('XL'), fontWeight: FONT_WEIGHTS.BOLD }}>
              FARO Console
            </span>
          }
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={LAYOUT_CONSTANTS.DRAWER_WIDTH}
          styles={{
            header: {
              background: CONSOLE_THEME.BACKGROUND,
              borderBottom: `1px solid ${CONSOLE_THEME.BORDER}`
            },
            body: {
              padding: 0,
              background: CONSOLE_THEME.BACKGROUND
            },
          }}
          closeIcon={<span style={{ color: CONSOLE_THEME.TEXT }}>✕</span>}
        >
          {renderSideMenu()}
        </Drawer>
      )}

      <Layout>
        <Header className="bg-white px-4 flex items-center justify-between shadow-sm">
          {isMobile ? (
            <button
              onClick={() => setDrawerVisible(!drawerVisible)}
              className="text-lg hover:bg-gray-100 p-2 rounded"
            >
              <MenuOutlined />
            </button>
          ) : (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-lg hover:bg-gray-100 p-2 rounded"
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
          )}

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space className="cursor-pointer">
              <Avatar icon={<UserOutlined />} />
              {!isMobile && (
                <div className="flex flex-col">
                  <Text strong>{user?.userName}</Text>
                  <Text type="secondary" className="text-xs">
                    {user?.role?.roleName}
                  </Text>
                </div>
              )}
            </Space>
          </Dropdown>
        </Header>

        <Content className={isMobile ? "m-3 p-3 bg-white rounded-lg shadow-sm" : "m-6 p-6 bg-white rounded-lg shadow-sm"}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};
